import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import ProfileImage from './ProfileImage';
import WorkspacePill from './WorkspacePill';
import LoginButton from './LoginButton';
import { useAuth } from '../context/AuthContext';
import { useUsageVisibility } from '../hooks/useUsageVisibility';
import { useLiveProfileName } from '../hooks/useLiveProfileName';
import NotificationsBell from './NotificationsBell';
import MobileNotifications from './MobileNotifications';
import { countAccountLevelUnread, subscribeNotifications } from '../repositories/notificationsRepository';
import { BRAND_MARK_SRC } from '../config/brandMark.generated';
import { APP_HEADER_Z_INDEX } from '../styles/layers';
import { appNavLinks } from '../lib/appNavLinks';

// Add a global style to completely hide profile images in mobile view
const MobileProfileHider = styled.div`
  @media (max-width: 768px) {
    /* Target any ProfileImage rendered in the header */
    && > div[class*="ProfileImage"],
    && > div[class*="ProfileContainer"],
    && *[class*="profile-image"],
    && *[class*="ProfilePicture"] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      height: 0 !important;
      width: 0 !important;
      overflow: hidden !important;
    }
  }
`;

const HeaderContainer = styled.header`
  background: white;
  padding: 1.25rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0,0,0,0.05);
  position: relative;
  z-index: ${APP_HEADER_Z_INDEX};
  
  @media (max-width: 1024px) {
    padding: 1rem 1.5rem;
  }
`;

const Logo = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.75rem;
  color: var(--brand-text);
  text-decoration: none;
  margin-right: 2rem;
  white-space: nowrap;
  border: none;
  outline: none;
  box-shadow: none;
  background: transparent;
  position: relative;
  padding: 0;
  padding-bottom: 10px;
  z-index: 1;
  letter-spacing: -0.03em;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1), filter 220ms ease, color 220ms ease;

  &::before {
    display: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background: linear-gradient(135deg, var(--brand-gold-alt), var(--brand-black));
    border-radius: 3px;
    transition: all 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px) scale(1.02);
    filter: drop-shadow(0 6px 16px rgba(200, 167, 94, 0.12));
    color: #0e0e0e;

    &::after {
      width: 60px;
      background: linear-gradient(135deg, #B8924C, var(--brand-black));
    }
  }

  &:active {
    transform: translateY(0) scale(0.985);
    filter: drop-shadow(0 2px 8px rgba(200, 167, 94, 0.08));
  }
`;

const HeaderInlineLogo = styled.img`
  display: inline-block;
  height: 1.1em;
  margin-left: -0.12em;
  margin-bottom: -0.15em;
  vertical-align: baseline;
  object-fit: contain;
  filter: drop-shadow(0 2px 6px rgba(200, 167, 94, 0.15));
`;

const LogoText = styled.span`
  display: inline-block;
  line-height: 1;
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
  flex-wrap: nowrap;
  justify-content: flex-end;
  width: auto;
  
  @media (max-width: 1024px) {
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileMenuToggle = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: none;
  padding: 0.5rem;
  margin-left: auto;
  z-index: 15001;
  position: relative;
  min-width: 44px;
  min-height: 44px;
  
  svg {
    width: 24px;
    height: 24px;
  }
  
  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const MobileMenuBadgeCounter = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--brand-danger);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #fff, 0 6px 12px rgba(226, 74, 74, 0.35);
  pointer-events: none;
`;

const MobileMenu = styled.div<{ $isOpen: boolean }>`
  display: none;
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 220px;
  flex-direction: column;
  z-index: 200000; /* ensure above prompt bars and page overlays */
  transform: ${props => props.$isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: all 0.2s ease;
  
  @media (max-width: 768px) {
    display: flex;
    width: min(280px, calc(100vw - 1rem));
    right: 0.5rem;
    max-height: calc(100vh - 4.5rem);
    overflow-y: auto;
  }
`;

const MobileProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.25rem 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(0, 0, 0, 0.02);
`;

const MobileProfilePicture = styled.div<{ size: number }>`
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  margin-bottom: 0.75rem;
  border: 2px solid white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  svg {
    width: 60%;
    height: 60%;
    color: var(--brand-text-muted);
  }
`;

const MobileUserName = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: #333;
  margin-bottom: 0.25rem;
`;

const MobileUserEmail = styled.div`
  font-size: 0.875rem;
  color: #666;
  overflow-wrap: anywhere;
  text-align: center;
`;

const MobileInitials = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const MobileNavLink = styled(Link)<{ $active?: boolean }>`
  color: ${props => props.$active ? '#333' : '#666'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  min-height: 44px;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  overflow-wrap: anywhere;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    color: #333;
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 1rem;
    opacity: ${props => props.$active ? '1' : '0.7'};
  }
  
  &.icon-only {
    justify-content: center;
    
    svg {
      margin-right: 0;
    }
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const MobileNavButton = styled.div`
  color: #666;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  min-height: 44px;
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  cursor: pointer;
  overflow-wrap: anywhere;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
    color: #333;
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 1rem;
    opacity: 0.7;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  display: ${props => props.isOpen ? 'block' : 'none'};
  z-index: 199000; /* keep beneath menu but above page UI like prompt bars */
  
  @media (min-width: 769px) {
    display: none;
  }
`;

const NavLink = styled(Link)<{ $active?: boolean }>`
  color: ${props => props.$active ? '#333' : '#666'};
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.85rem;
  text-align: center;
  border-radius: 8px;
  position: relative;
  white-space: nowrap;
  min-width: 36px;
  
  &:hover {
    color: #333;
    background-color: rgba(0, 0, 0, 0.03);
  }

  svg {
    width: 18px;
    height: 18px;
    opacity: ${props => props.$active ? '1' : '0.7'};
    transition: opacity 0.2s ease;
    margin-right: ${props => props.children ? '0.5rem' : '0'};
    flex-shrink: 0;
  }

  &.icon-only {
    padding: 0.5rem;
    min-width: 44px;
    justify-content: center;
    
    svg {
      margin-right: 0;
    }
  }

  &:hover svg {
    opacity: 1;
  }
  
  @media (max-width: 1024px) {
    font-size: 0.85rem;
    padding: 0.5rem 0.7rem;
    
    svg {
      width: 16px;
      height: 16px;
      margin-right: ${props => props.children ? '0.375rem' : '0'};
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem;
    gap: 0.375rem;
  }
`;

const TooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  white-space: nowrap;
  margin-top: 8px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%) rotate(45deg);
    width: 8px;
    height: 8px;
    background-color: rgba(0, 0, 0, 0.8);
  }
  
  ${TooltipWrapper}:hover & {
    opacity: 1;
    visibility: visible;
  }
`;

const MobileLogoutButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.5rem;
  width: 100%;
  min-height: 44px;
  text-align: left;
  font-size: 0.95rem;
  font-weight: 500;
  color: #ff4d4f;
  cursor: pointer;
  transition: all 0.2s ease;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
  
  &:hover {
    background-color: rgba(255, 77, 79, 0.05);
  }
  
  svg {
    width: 18px;
    height: 18px;
    margin-right: 1rem;
    opacity: 0.7;
  }
  
  &:hover svg {
    opacity: 1;
  }
`;

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useAuth();
  // ORG-USAGE-2026: nav visibility for the usage/billing surfaces. Fail-open —
  // both flags are true while RBAC loads, on error, and for accounts that
  // predate the permission model.
  const { canSeeActivity, canSeeWallet } = useUsageVisibility();
  const [menuOpen, setMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // The user's REAL first + last name, live from the profile doc — the same
  // sourcing the chat sidebar uses. AuthContext's `name` is a login-time
  // snapshot that many accounts seeded from their email ("Fernandohidalgoesq"),
  // so it is only the last-resort fallback here.
  const [profileUid, setProfileUid] = useState<string | null>(null);
  useEffect(() => {
    const auth = getAuth();
    return onAuthStateChanged(auth, (fbUser) => {
      setProfileUid(fbUser?.uid || null);
    });
  }, []);
  const liveProfileName = useLiveProfileName(profileUid);
  const displayFirstName = (liveProfileName?.firstName || user?.firstName || '').trim();
  const displayLastName = (liveProfileName?.lastName || user?.lastName || '').trim();
  const displayName = liveProfileName?.fullName
    || ((user?.firstName && user?.lastName) ? `${user.firstName} ${user.lastName}` : (user?.name || ''));
  
  // Log the current path
  useEffect(() => {
    console.log('Header rendered with path:', location.pathname);
  }, [location.pathname]);
  
  // Close menu when location changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuOpen) {
        setMenuOpen(false);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuOpen]);

  // Hamburger badge (2026-09-08): the SAME Firestore feed the bell reads,
  // counted account-level (thread-scoped rows belong to their thread's own
  // badge). The old reader parsed a legacy localStorage store nothing writes
  // since the Firestore migration — the badge could only ever say 0.
  useEffect(() => {
    if (!isAuthenticated || !user?.id || !user?.accountId) {
      setUnreadCount(0);
      return undefined;
    }
    const unsubscribe = subscribeNotifications(
      user.id,
      (notifications) => setUnreadCount(countAccountLevelUnread(notifications)),
      () => setUnreadCount(0),
    );
    return unsubscribe;
  }, [isAuthenticated, user?.id, user?.accountId]);
  
  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(!menuOpen);
  };
  
  // Navigation items to be used in both desktop and mobile views
  const renderNavItems = (isMobile = false) => {
    const LinkComponent = isMobile ? MobileNavLink : NavLink;
    const currentPath = location.pathname;
    
    // Which links are offered, and in what order, is one rule shared with the new design's app header
    // (src/lib/appNavLinks.ts); this header keeps its own markup for each.
    const offered = new Set(appNavLinks({
      path: currentPath,
      isAuthenticated,
      canSeeWallet,
      canSeeActivity,
      phone: isMobile,
    }).map((link) => link.key));

    // List of paths that should show text in desktop view
    const textPaths = [
      '/user/profile',
      '/user/settings',
      '/user/wallet',
      '/activity',
      '/profile',
      '/settings',
      '/desktop',
      '/security'
    ];
    
    const securityIconOnlyPrefixes = [
      '/wallet',
      '/user/wallet',
      '/activity',
      '/profile',
      '/user/profile',
      '/settings',
      '/user/settings',
      '/notifications'
    ];
    
    const isSecurityIconOnly = securityIconOnlyPrefixes.some((prefix) => currentPath.startsWith(prefix));
    
    // Add a function to conditionally show labels in desktop view
    const shouldShowLabel = (path: string) => {
      // Always show labels in mobile menu (even when icon-only on desktop)
      if (isMobile) return true;
      if (path === '/security' && isSecurityIconOnly) {
        return false;
      }
      
      // Only show labels for specified paths in desktop
      if (textPaths.includes(path)) return true;
      
      // For category paths, check if current path starts with them
      return (
        (path === '/about' && currentPath.startsWith('/about')) ||
        (path === '/insights' && currentPath.startsWith('/insights')) ||
        (path === '/legal' && currentPath.startsWith('/legal')) ||
        (path === '/pricing' && currentPath.startsWith('/pricing'))
      );
    };
    
    // Function to get tooltip text for navigation item
    const getTooltipText = (path: string) => {
      switch(path) {
        case '/team': return 'team';
        case '/mission': return 'mission';
        case '/insights': return 'insights';
        case '/security': return 'security';
        case '/pricing': return 'pricing';
        case '/': return 'home';
        default: return '';
      }
    };
    
    // Paths that should have tooltips
    const tooltipPaths = [
      '/team',
      '/mission',
      '/insights',
      '/',
      '/security',
      '/pricing'
    ];
    
    // Function to determine if a button should be icon-only
    const getClassName = (path: string) => {
      // Always allow full labels in mobile dropdowns for consistent alignment
      if (isMobile) return '';
      if (path === '/security' && isSecurityIconOnly) {
        return 'icon-only';
      }
      return !textPaths.includes(path) && 
        !(['/about', '/insights', '/legal'].some(p => path === p && currentPath.startsWith(p)))
        ? 'icon-only' : '';
    };
    
    // Function to check if a path is active
    const isPathActive = (path: string) => {
      if (path === '/') return currentPath === '/';
      return currentPath.startsWith(path);
    };
    
    // Render a nav item with optional tooltip
    const renderNavItem = (path: string, active: boolean, className: string, icon: React.ReactNode, label: React.ReactNode) => {
      // Should this item have a tooltip?
      const needsTooltip = !isMobile && tooltipPaths.includes(path) && !shouldShowLabel(path);

      /**
       * The accessible name, always — 2026-09-12, WCAG 2.4.4 / 4.1.2.
       *
       * When shouldShowLabel() is false this link renders an SVG and nothing
       * else: no text, no title, no aria-label. A screen reader announced five
       * of these in a row as "link", "link", "link", with no way to tell the
       * team page from pricing, and axe flagged 25 of them across five pages —
       * the whole site navigation, on every page that is not home. A nameless
       * link is one of the five failures web-accessibility complaints are
       * actually written about.
       *
       * The tooltip already held the right word for exactly this case; it was
       * simply shown to sighted users only. Now it names the link for everyone.
       * Where the visible text IS shown, the name equals it exactly, which is
       * what WCAG 2.5.3 (Label in Name) asks for — so this is safe to set
       * unconditionally rather than only when the label is hidden.
       */
      const accessibleName = typeof label === 'string' && label
        ? label
        : getTooltipText(path);

      if (needsTooltip) {
        return (
          <TooltipWrapper>
            <LinkComponent 
              to={path} 
              $active={active} 
              className={className}
              aria-label={accessibleName || undefined}
            >
              {icon}
              {shouldShowLabel(path) && label}
            </LinkComponent>
            <Tooltip>{getTooltipText(path)}</Tooltip>
          </TooltipWrapper>
        );
      }
      
      return (
        <LinkComponent 
          to={path} 
          $active={active} 
          className={className}
          aria-label={accessibleName || undefined}
        >
          {icon}
          {shouldShowLabel(path) && label}
        </LinkComponent>
      );
    };
    
    // Render authenticated items only when logged in
    const authNavItems = isAuthenticated ? (
      <>
        {offered.has('desktop') && (
          <LinkComponent
            to="/desktop"
            $active={currentPath === '/desktop'}
            className={getClassName('/desktop')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
            {shouldShowLabel('/desktop') && "desktop"}
          </LinkComponent>
        )}

        {offered.has('wallet') && (
          <LinkComponent 
            to="/wallet" 
            $active={currentPath === '/user/wallet' || currentPath === '/wallet' || currentPath === '/crypto/wallet/user-wallet'} 
            className={getClassName('/user/wallet')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M16 12h.01"/>
              <path d="M2 10h20"/>
            </svg>
            {shouldShowLabel('/user/wallet') && "wallet"}
          </LinkComponent>
        )}

        {/* ORG-USAGE-2026: the activity link appears for members whose role
            grants it. FAIL-OPEN — while RBAC loads, on error, and for accounts
            that predate the permission model, the link shows (see
            hooks/usageVisibility.ts), so nobody loses navigation on deploy. */}
        {offered.has('activity') && (
          <LinkComponent
            to="/activity"
            $active={currentPath === '/activity'}
            className={getClassName('/activity')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3v18h18"/>
              <path d="M7 15l4-4 3 3 5-6"/>
            </svg>
            {shouldShowLabel('/activity') && "activity"}
          </LinkComponent>
        )}
        
        {/* Profile and Settings are in the avatar dropdown — not the top bar */}

        
      </>
    ) : null;
    
    // Always show these items
    return (
      <>
        {offered.has('team') && renderNavItem(
          '/team',
          currentPath === '/team',
          getClassName('/team'),
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
          </svg>,
          "team"
        )}

        {offered.has('mission') && renderNavItem(
          '/mission',
          isPathActive('/mission'),
          getClassName('/mission'),
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
          </svg>,
          "mission"
        )}

        {offered.has('insights') && renderNavItem(
          '/insights',
          isPathActive('/insights'),
          getClassName('/insights'),
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6" />
            <path d="M5 10a7 7 0 0 1 14 0c0 3.07-1.63 4.68-3 5.58a2 2 0 0 0-1 1.7V19a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-1.72a2 2 0 0 0-1-1.7C6.63 14.68 5 13.07 5 10z" />
            <line x1="9" y1="22" x2="15" y2="22" />
          </svg>,
          "insights"
        )}
        {offered.has('pricing') && renderNavItem(
          '/pricing',
          isPathActive('/pricing'),
          getClassName('/pricing'),
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="1" x2="12" y2="23" />
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
          </svg>,
          "pricing"
        )}


        {/* Show authenticated nav items after primary links */}
        {authNavItems}

        {offered.has('home') && renderNavItem(
          '/',
          currentPath === '/',
          getClassName('/'),
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
            <polyline points="9 22 9 12 15 12 15 22"/>
          </svg>,
          "home"
        )}

      </>
    );
  };
  
  return (
    <MobileProfileHider>
      <HeaderContainer>
        <Logo to={isAuthenticated ? "/desktop" : "/"}>
          <LogoText>prest<HeaderInlineLogo src={BRAND_MARK_SRC} alt="0" /></LogoText>
        </Logo>
        
        {/* Desktop navigation. Every top-bar control (nav links, bell, avatar,
            login) lives inside Nav so the flex gap spaces them all evenly. */}
        <Nav>
          {renderNavItems()}
          {/* Notifications bell - show on all authenticated pages */}
          {isAuthenticated && (
            <NotificationsBell />
          )}
          {/* Show Profile Image when authenticated, Login Button when not */}
          {/* Multi-org P3 (2026-08-16): persistent workspace identity + switcher.
              Renders nothing until the verified workspace list resolves. */}
          {isAuthenticated ? (
            <>
              <WorkspacePill />
              <ProfileImage size={36} />
            </>
          ) : <LoginButton />}
        </Nav>
        
        {/* Mobile menu toggle.
            2026-09-12: it renders an SVG and nothing else, so it reached a
            screen reader as an unnamed button — axe flagged it on every page.
            The name states what pressing it DOES, which is what a button's
            name is for, and aria-expanded lets a reader know which of the two
            it is about to do without seeing the icon change. */}
        <MobileMenuToggle
          onClick={toggleMenu}
          aria-label={menuOpen ? 'close menu' : 'open menu'}
          aria-expanded={menuOpen}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {menuOpen ? (
              <path d="M18 6L6 18M6 6l12 12" />
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
          {unreadCount > 0 && (
            <MobileMenuBadgeCounter data-menu-unread-count={unreadCount}>{unreadCount > 99 ? '99+' : unreadCount}</MobileMenuBadgeCounter>
          )}
        </MobileMenuToggle>
        
        {/* Mobile menu dropdown */}
        <MobileMenu $isOpen={menuOpen} onClick={(e) => e.stopPropagation()}>
          {/* Add profile section when user is authenticated */}
          {isAuthenticated && user && location.pathname !== '/wallet' && location.pathname !== '/profile' && location.pathname !== '/settings' && (
            <MobileProfileSection>
              <MobileProfilePicture size={60}>
                {user.profileImage ? (
                  <img
                    src={user.profileImage}
                    alt={user.name}
                    loading="lazy"
                    width={60}
                    referrerPolicy="no-referrer"
                    height={60}
                  />
                ) : (displayFirstName.length > 0 && displayLastName.length > 0) ? (
                  <MobileInitials>
                    {`${displayFirstName[0]}${displayLastName[0]}`.toLowerCase()}
                  </MobileInitials>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
              </MobileProfilePicture>
              <MobileUserName>{displayName}</MobileUserName>
              <MobileUserEmail>{user.email}</MobileUserEmail>
            </MobileProfileSection>
          )}
          {renderNavItems(true)}
          {/* Mobile notifications section - consistent placement and UX */}
          {isAuthenticated && (
            <MobileNotifications closeMenu={() => setMenuOpen(false)} />
          )}
          {/* Logout is always the LAST row — destructive actions never sit
              between navigation and notifications. */}
          {isAuthenticated && (
            <MobileLogoutButton onClick={() => logout()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              logout
            </MobileLogoutButton>
          )}
          {/* Show Login Button in mobile menu when not authenticated */}
          {!isAuthenticated && (
            <MobileNavButton onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              setMenuOpen(false);
              // Login default landing is the chat surface, not /desktop.
              navigate('/auth', { state: { returnUrl: '/' } });
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </svg>
              login
            </MobileNavButton>
          )}
        </MobileMenu>
        
        {/* Overlay for closing the menu when clicking outside */}
        <Overlay isOpen={menuOpen} onClick={() => setMenuOpen(false)} />
      </HeaderContainer>
    </MobileProfileHider>
  );
};

export default Header; 
